import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { money, DAYS, dayLabel, fmtTime } from "@/lib/format";
import { Store, Truck, Building2, Tag, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { settings } = useAuth();
  const nav = useNavigate();

  const [cust, setCust] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [notes, setNotes] = useState("");
  const [method, setMethod] = useState("");
  const [details, setDetails] = useState({});
  const [slots, setSlots] = useState([]);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [addrStatus, setAddrStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!settings) return;
    const avail = [];
    if (settings.pickup_enabled) avail.push("pickup");
    if (settings.local_delivery_enabled) avail.push("local_delivery");
    if (settings.carmax_enabled) avail.push("carmax_delivery");
    setMethod((m) => (m && avail.includes(m) ? m : avail[0] || ""));
  }, [settings]);

  useEffect(() => {
    if (method === "local_delivery") api.get("/delivery/slots").then((r) => setSlots(r.data)).catch(() => {});
  }, [method]);

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl text-cream mb-4">Your cart is empty</h1>
        <Button className="btn-gold rounded-full px-8 py-6" onClick={() => nav("/shop")}>Shop Now</Button>
      </div>
    );
  }

  const setD = (k, v) => setDetails((p) => ({ ...p, [k]: v }));
  const setD2 = (patch) => setDetails((p) => ({ ...p, ...patch }));

  const checkAddress = async () => {
    if (!details.zip) return;
    try {
      const { data } = await api.post("/checkout/validate-address", { zip: details.zip });
      if (data.available) {
        setDeliveryFee(data.fee);
        setAddrStatus({ ok: true, msg: `${data.zone_name} · Delivery fee ${money(data.fee)} · Min order ${money(data.min_order)}` });
      } else {
        setDeliveryFee(0);
        setAddrStatus({ ok: false, msg: data.message });
      }
    } catch (e) {
      setAddrStatus({ ok: false, msg: apiErr(e) });
    }
  };

  const applyPromo = async () => {
    if (!promoCode) return;
    try {
      const { data } = await api.post("/promo/validate", { code: promoCode, subtotal });
      setPromo(data);
      toast.success(`Promo ${data.code} applied!`);
    } catch (e) {
      setPromo(null);
      toast.error(apiErr(e));
    }
  };

  const effectiveDeliveryFee = method === "local_delivery" ? (promo?.free_delivery ? 0 : deliveryFee) : 0;
  const discount = promo && !promo.free_delivery ? promo.discount : 0;
  const total = Math.max(0, subtotal - discount) + effectiveDeliveryFee;

  const validate = () => {
    if (!cust.first_name || !cust.last_name || !cust.phone) {
      toast.error("Please enter your name and phone number");
      return false;
    }
    if (method === "local_delivery") {
      if (!details.street || !details.city || !details.state || !details.zip) {
        toast.error("Please complete your delivery address");
        return false;
      }
      if (addrStatus && !addrStatus.ok) {
        toast.error("Delivery is unavailable for this ZIP. Please contact us.");
        return false;
      }
      if (!details.slot_key) {
        toast.error("Please choose a delivery day and time window");
        return false;
      }
    }
    if (method === "carmax_delivery") {
      if (!details.delivery_day || !details.department) {
        toast.error("Please provide your CarMax delivery day and department");
        return false;
      }
    }
    return true;
  };

  const placeOrder = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...cust,
        items: items.map((i) => ({
          product_id: i.product_id,
          size_label: i.category === "cookie" ? null : i.size_label,
          quantity: i.quantity,
          cookie_flavors: i.cookie_flavors || [],
        })),
        special_instructions: notes,
        fulfillment_method: method,
        fulfillment_details: details,
        promo_code: promo?.code || null,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      nav(`/payment/${data.order_number}`, { state: { order: data } });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  const availableDays = DAYS.filter((d) => settings?.delivery_schedule?.[d]?.available);

  const methods = [
    { id: "pickup", icon: Store, label: "Local Pickup", desc: "Free pickup at our kitchen", badge: "FREE", enabled: settings?.pickup_enabled },
    { id: "local_delivery", icon: Truck, label: "Local Delivery", desc: "Delivered to your door", badge: "By ZIP", enabled: settings?.local_delivery_enabled },
    { id: "carmax_delivery", icon: Building2, label: "CarMax Workplace Delivery", desc: "Drop-off at CarMax", badge: "SPECIAL", enabled: settings?.carmax_enabled },
  ].filter((m) => m.enabled);

  const field = "bg-input border-gold/25 text-cream placeholder:text-cream/30 rounded-xl h-12";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-cream mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Customer info */}
          <section className="gold-border-card rounded-xl bg-card-surface p-6">
            <h2 className="font-serif text-xl text-gold mb-4">Your Information</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-cream/70 text-sm">First Name *</Label><Input className={field} value={cust.first_name} onChange={(e) => setCust({ ...cust, first_name: e.target.value })} data-testid="checkout-first-name" /></div>
              <div><Label className="text-cream/70 text-sm">Last Name *</Label><Input className={field} value={cust.last_name} onChange={(e) => setCust({ ...cust, last_name: e.target.value })} data-testid="checkout-last-name" /></div>
              <div><Label className="text-cream/70 text-sm">Phone *</Label><Input type="tel" className={field} value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} data-testid="checkout-phone" /></div>
              <div><Label className="text-cream/70 text-sm">Email (optional)</Label><Input type="email" className={field} value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} data-testid="checkout-email" /></div>
            </div>
          </section>

          {/* Fulfillment */}
          <section className="gold-border-card rounded-xl bg-card-surface p-6">
            <h2 className="font-serif text-xl text-gold mb-4">How would you like your order?</h2>
            <div className="grid gap-3">
              {methods.map((m) => (
                <button key={m.id} onClick={() => { setMethod(m.id); setAddrStatus(null); }} data-testid={`fulfillment-option-${m.id === "carmax_delivery" ? "carmax" : m.id === "local_delivery" ? "delivery" : "pickup"}`}
                  className={`flex items-center gap-4 rounded-xl border p-4 text-left transition-all ${method === m.id ? "border-gold bg-gold/10" : "border-gold/20 hover:border-gold/50"}`}>
                  <m.icon className={method === m.id ? "text-gold" : "text-cream/60"} size={26} />
                  <div className="flex-1">
                    <p className="text-cream font-semibold">{m.label}</p>
                    <p className="text-cream/50 text-sm">{m.desc}</p>
                  </div>
                  <span className="btn-gold text-[10px] font-bold px-2.5 py-1 rounded-full">{m.badge}</span>
                </button>
              ))}
            </div>

            {/* Local delivery fields */}
            {method === "local_delivery" && (
              <div className="mt-5 grid sm:grid-cols-2 gap-4" data-testid="local-delivery-fields">
                <div className="sm:col-span-2"><Label className="text-cream/70 text-sm">Street Address *</Label><Input className={field} value={details.street || ""} onChange={(e) => setD("street", e.target.value)} data-testid="delivery-street" /></div>
                <div><Label className="text-cream/70 text-sm">Apartment / Unit</Label><Input className={field} value={details.apt || ""} onChange={(e) => setD("apt", e.target.value)} data-testid="delivery-apt" /></div>
                <div><Label className="text-cream/70 text-sm">City *</Label><Input className={field} value={details.city || ""} onChange={(e) => setD("city", e.target.value)} data-testid="delivery-city" /></div>
                <div><Label className="text-cream/70 text-sm">State *</Label><Input className={field} value={details.state || ""} onChange={(e) => setD("state", e.target.value)} data-testid="delivery-state" /></div>
                <div><Label className="text-cream/70 text-sm">ZIP Code *</Label><Input className={field} value={details.zip || ""} onChange={(e) => setD("zip", e.target.value)} onBlur={checkAddress} data-testid="delivery-zip" /></div>
                <div className="sm:col-span-2"><Label className="text-cream/70 text-sm">Delivery Instructions</Label><Input className={field} value={details.instructions || ""} onChange={(e) => setD("instructions", e.target.value)} data-testid="delivery-instructions" /></div>
                {addrStatus && (
                  <p className={`sm:col-span-2 text-sm ${addrStatus.ok ? "text-emerald-400" : "text-destructive"}`} data-testid="address-status">{addrStatus.msg}</p>
                )}
                <div className="sm:col-span-2" data-testid="delivery-slot-picker">
                  <Label className="text-cream/70 text-sm">Delivery Day *</Label>
                  <select className={`w-full ${field} px-3`} value={details.delivery_date || ""} onChange={(e) => setD2({ delivery_date: e.target.value, delivery_window: null, delivery_time: "", slot_key: "" })} data-testid="delivery-day-select">
                    <option value="">Select a delivery day</option>
                    {slots.map((s) => <option key={s.date} value={s.date}>{new Date(s.date + "T12:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</option>)}
                  </select>
                  {details.delivery_date && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {(slots.find((s) => s.date === details.delivery_date)?.windows || []).map((w) => {
                        const key = `${details.delivery_date}|${w.start}-${w.end}`;
                        const sel = details.slot_key === key;
                        return (
                          <button type="button" key={key} disabled={w.full}
                            onClick={() => setD2({ delivery_window: { start: w.start, end: w.end }, delivery_time: `${fmtTime(w.start)} – ${fmtTime(w.end)}`, slot_key: key })}
                            data-testid={`slot-${key}`}
                            className={`text-sm px-3 py-2 rounded-lg border transition-all ${sel ? "border-gold bg-gold/15 text-gold" : "border-gold/25 text-cream/70 hover:border-gold/50"} ${w.full ? "opacity-40 cursor-not-allowed line-through" : ""}`}>
                            {fmtTime(w.start)} – {fmtTime(w.end)} {w.full ? "(Full)" : w.remaining <= 3 ? `(${w.remaining} left)` : ""}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {slots.length === 0 && <p className="text-cream/50 text-sm mt-2">No delivery times are currently available. Please contact us.</p>}
                </div>
              </div>
            )}

            {/* CarMax fields */}
            {method === "carmax_delivery" && (
              <div className="mt-5 grid sm:grid-cols-2 gap-4" data-testid="carmax-fields">
                <p className="sm:col-span-2 text-cream/60 text-sm">{settings?.carmax_instructions}</p>
                <div>
                  <Label className="text-cream/70 text-sm">Preferred Delivery Day *</Label>
                  <select className={`w-full ${field} px-3`} value={details.delivery_day || ""} onChange={(e) => setD("delivery_day", e.target.value)} data-testid="carmax-day">
                    <option value="">Select a day</option>
                    {(availableDays.length ? availableDays : DAYS).map((d) => <option key={d} value={d}>{dayLabel(d)}</option>)}
                  </select>
                </div>
                <div><Label className="text-cream/70 text-sm">Preferred Time *</Label><Input className={field} placeholder="e.g. Lunch / 12 PM" value={details.delivery_time || ""} onChange={(e) => setD("delivery_time", e.target.value)} data-testid="carmax-time" /></div>
                <div className="sm:col-span-2"><Label className="text-cream/70 text-sm">Department / Meeting Location *</Label><Input className={field} placeholder="e.g. Sales floor, Break room" value={details.department || ""} onChange={(e) => setD("department", e.target.value)} data-testid="carmax-department" /></div>
                <div className="sm:col-span-2"><Label className="text-cream/70 text-sm">Special Instructions</Label><Input className={field} placeholder="e.g. Bring my cookies when you come to work Tuesday" value={details.carmax_notes || ""} onChange={(e) => setD("carmax_notes", e.target.value)} data-testid="carmax-notes" /></div>
              </div>
            )}

            {method === "pickup" && (
              <p className="mt-4 text-cream/60 text-sm">{settings?.pickup_instructions}</p>
            )}
          </section>

          {/* Notes */}
          <section className="gold-border-card rounded-xl bg-card-surface p-6">
            <h2 className="font-serif text-xl text-gold mb-4">Special Instructions</h2>
            <Textarea className="bg-input border-gold/25 text-cream placeholder:text-cream/30 rounded-xl" placeholder="Any allergies, requests, or notes for your order..." value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="checkout-special-instructions" />
          </section>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="gold-border-card rounded-xl bg-card-surface p-6 sticky top-24">
            <h2 className="font-serif text-xl text-gold mb-4">Order Summary</h2>
            <div className="space-y-2 max-h-52 overflow-y-auto no-scrollbar mb-4">
              {items.map((it) => (
                <div key={it.key} className="flex justify-between text-sm text-cream/80">
                  <span className="truncate mr-2">{it.quantity}× {it.name === "Cookie Pair (Mix & Match)" ? it.size_label : `${it.name} (${it.size_label})`}</span>
                  <span className="font-mono whitespace-nowrap">{money(it.unit_price * it.quantity)}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mb-4">
              <Input className="bg-input border-gold/25 text-cream rounded-xl h-11" placeholder="Promo code" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} data-testid="promo-code-input" />
              <Button variant="outline" className="border-gold/40 text-gold bg-transparent hover:bg-gold/10 rounded-xl h-11" onClick={applyPromo} data-testid="apply-promo-btn"><Tag size={15} /></Button>
            </div>

            <div className="space-y-2 border-t border-gold/20 pt-4 text-sm">
              <div className="flex justify-between text-cream/80"><span>Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-emerald-400"><span>Discount ({promo.code})</span><span className="font-mono">-{money(discount)}</span></div>}
              {method === "local_delivery" && <div className="flex justify-between text-cream/80"><span>Delivery Fee{promo?.free_delivery && " (free)"}</span><span className="font-mono">{money(effectiveDeliveryFee)}</span></div>}
              <div className="flex justify-between text-cream font-semibold text-lg border-t border-gold/20 pt-3"><span>Total</span><span className="font-mono text-gold" data-testid="checkout-total">{money(total)}</span></div>
            </div>

            <Button className="btn-gold w-full rounded-full py-6 mt-5 font-semibold" onClick={placeOrder} disabled={submitting} data-testid="place-order-btn">
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <span className="flex items-center gap-2"><CheckCircle2 size={18} /> Place Order</span>}
            </Button>
            <p className="text-cream/40 text-xs text-center mt-3">Pay with Cash App on the next screen.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
